import { useState } from 'react'
import { Download, Upload, Chrome, FileJson, FileText, Table, AlertCircle, Settings, Check, X } from 'lucide-react'
import { Button } from './Button'
import { useBookmarkStore } from '../store/bookmarkStore'
import { BrowserBookmarkImport } from './BrowserBookmarkImport'
import { BookmarkExporter } from '../utils/exportUtils'
import { BookmarkImporter, ImportResult } from '../utils/importUtils'
import { Dialog } from './Dialog'
import { Checkbox } from './Checkbox'

type ExportFormat = 'json' | 'html' | 'csv'
type ImportMode = 'append' | 'overwrite' | 'skip'

interface ImportProgress {
  total: number
  current: number
  status: 'processing' | 'success' | 'error'
  message?: string
}

interface ExportOptions {
  includeMetadata: boolean
  dateFormat: string
  customFilename: string
  selectedCategories: string[]
  selectedTags: string[]
}

interface ImportPreview extends ImportResult {
  selected: {
    bookmarks: Set<number>
    categories: Set<number>
  }
}

export function ImportExport() {
  const { bookmarks, categories, importData } = useBookmarkStore()
  const [showBrowserImport, setShowBrowserImport] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showExportOptions, setShowExportOptions] = useState(false)
  const [showImportPreview, setShowImportPreview] = useState(false)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const [importMode, setImportMode] = useState<ImportMode>('append')
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeMetadata: true,
    dateFormat: 'yyyy-MM-dd HH:mm:ss',
    customFilename: '',
    selectedCategories: [],
    selectedTags: []
  })

  const handleExport = (format: ExportFormat) => {
    const exporter = new BookmarkExporter(
      // 根据选择过滤书签
      bookmarks.filter(b => {
        if (exportOptions.selectedCategories.length > 0 && !exportOptions.selectedCategories.includes(b.categoryId || '')) {
          return false
        }
        if (exportOptions.selectedTags.length > 0 && !b.tags.some(t => exportOptions.selectedTags.includes(t))) {
          return false
        }
        return true
      }),
      // 根据选择过滤分类
      categories.filter(c => 
        exportOptions.selectedCategories.length === 0 || exportOptions.selectedCategories.includes(c.id)
      )
    )

    const { content, filename } = exporter.export({ 
      format,
      includeMetadata: exportOptions.includeMetadata,
      dateFormat: exportOptions.dateFormat
    })

    // 创建下载链接
    const blob = new Blob([content], { type: `text/${format}` })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = exportOptions.customFilename || filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setShowExportMenu(false)
    setShowExportOptions(false)
  }

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files?.length) return

    try {
      setImportProgress({
        total: files.length,
        current: 0,
        status: 'processing',
        message: '准备导入...'
      })

      const importer = new BookmarkImporter()
      let totalBookmarks = 0
      let totalCategories = 0
      const allResults: ImportResult[] = []

      // 处理每个文件
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        setImportProgress(prev => ({
          ...prev!,
          current: i + 1,
          message: `正在处理文件 ${file.name}...`
        }))

        const content = await file.text()
        const format = file.name.toLowerCase().endsWith('.csv')
          ? 'csv'
          : file.name.toLowerCase().endsWith('.html') || file.name.toLowerCase().endsWith('.htm')
          ? 'html'
          : 'json'

        const result = importer.import(content, format)
        allResults.push(result)
        totalBookmarks += result.stats.totalBookmarks
        totalCategories += result.stats.totalCategories
      }

      // 设置导入预览
      const combinedResult = allResults.reduce((acc, curr) => ({
        bookmarks: [...acc.bookmarks, ...curr.bookmarks],
        categories: [...acc.categories, ...curr.categories],
        stats: {
          totalBookmarks: acc.stats.totalBookmarks + curr.stats.totalBookmarks,
          totalCategories: acc.stats.totalCategories + curr.stats.totalCategories,
          errors: [...acc.stats.errors, ...curr.stats.errors]
        }
      }), {
        bookmarks: [],
        categories: [],
        stats: {
          totalBookmarks: 0,
          totalCategories: 0,
          errors: []
        }
      } as ImportResult)

      setImportPreview({
        ...combinedResult,
        selected: {
          bookmarks: new Set(Array.from({ length: combinedResult.bookmarks.length }, (_, i) => i)),
          categories: new Set(Array.from({ length: combinedResult.categories.length }, (_, i) => i))
        }
      })
      setShowImportPreview(true)
      setImportProgress(null)
    } catch (error) {
      setImportProgress(prev => ({
        ...prev!,
        status: 'error',
        message: '导入失败：' + (error instanceof Error ? error.message : String(error))
      }))
      setTimeout(() => setImportProgress(null), 3000)
    }

    event.target.value = ''
  }

  const handleConfirmImport = () => {
    if (!importPreview) return

    const selectedBookmarks = Array.from(importPreview.selected.bookmarks)
      .map(i => ({
        ...importPreview.bookmarks[i],
        id: crypto.randomUUID()
      }))

    const selectedCategories = Array.from(importPreview.selected.categories)
      .map(i => ({
        ...importPreview.categories[i],
        id: crypto.randomUUID()
      }))

    if (importMode === 'append') {
      importData({
        bookmarks: [...bookmarks, ...selectedBookmarks],
        categories: [...categories, ...selectedCategories]
      })
    } else if (importMode === 'skip') {
      const existingUrls = new Set(bookmarks.map(b => b.url))
      importData({
        bookmarks: [...bookmarks, ...selectedBookmarks.filter(b => !existingUrls.has(b.url))],
        categories: [...categories, ...selectedCategories]
      })
    } else {
      importData({
        bookmarks: selectedBookmarks,
        categories: selectedCategories
      })
    }

    setShowImportPreview(false)
    setImportProgress({
      total: 1,
      current: 1,
      status: 'success',
      message: '导入完成！'
    })
    setTimeout(() => setImportProgress(null), 3000)
  }

  // 获取所有唯一标签
  const allTags = Array.from(new Set(bookmarks.flatMap(b => b.tags))).sort()

  return (
    <>
      <div className="space-y-4">
        <div className="flex space-x-2">
          <input
            type="file"
            accept=".json,.html,.htm,.csv"
            onChange={handleImport}
            className="hidden"
            id="import-file"
            multiple
          />
          <div className="flex flex-col">
            <Button
              variant="outline"
              onClick={() => document.getElementById('import-file')?.click()}
              className="flex items-center"
              title="导入书签数据"
            >
              <Upload className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">导入</span>
            </Button>
            <select
              className="mt-1 text-sm border rounded p-1"
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as ImportMode)}
            >
              <option value="append">追加模式</option>
              <option value="overwrite">覆盖模式</option>
              <option value="skip">跳过重复</option>
            </select>
          </div>
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center"
              title="导出书签数据"
            >
              <Download className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">导出</span>
            </Button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                <div className="py-1">
                  <button
                    onClick={() => setShowExportOptions(true)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    导出选项
                  </button>
                  <div className="border-t border-gray-200" />
                  <button
                    onClick={() => handleExport('json')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <FileJson className="w-4 h-4 mr-2" />
                    导出为 JSON
                  </button>
                  <button
                    onClick={() => handleExport('html')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    导出为 HTML
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Table className="w-4 h-4 mr-2" />
                    导出为 CSV
                  </button>
                </div>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => setShowBrowserImport(true)}
            className="flex items-center"
            title="从浏览器导入书签"
          >
            <Chrome className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">从浏览器导入</span>
          </Button>
        </div>

        {/* 导入进度显示 */}
        {importProgress && (
          <div className={`
            mt-2 p-2 rounded text-sm
            ${importProgress.status === 'error' ? 'bg-red-100 text-red-700' :
              importProgress.status === 'success' ? 'bg-green-100 text-green-700' :
              'bg-blue-100 text-blue-700'}
          `}>
            <div className="flex items-center">
              {importProgress.status === 'error' && <AlertCircle className="w-4 h-4 mr-1" />}
              <span>{importProgress.message}</span>
            </div>
            {importProgress.status === 'processing' && (
              <div className="mt-1">
                <div className="w-full bg-gray-200 rounded h-2">
                  <div
                    className="bg-blue-600 h-2 rounded"
                    style={{
                      width: `${(importProgress.current / importProgress.total) * 100}%`
                    }}
                  />
                </div>
                <div className="text-xs mt-1 text-gray-600">
                  {importProgress.current} / {importProgress.total} 个文件
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 导出选项对话框 */}
      {showExportOptions && (
        <Dialog
          title="导出选项"
          onClose={() => setShowExportOptions(false)}
        >
          <div className="space-y-4 p-4">
            <div className="flex items-center">
              <Checkbox
                checked={exportOptions.includeMetadata}
                onChange={(checked) => setExportOptions(prev => ({ ...prev, includeMetadata: checked }))}
                label="包含元数据"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">日期格式</label>
              <input
                type="text"
                value={exportOptions.dateFormat}
                onChange={(e) => setExportOptions(prev => ({ ...prev, dateFormat: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="yyyy-MM-dd HH:mm:ss"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">自定义文件名</label>
              <input
                type="text"
                value={exportOptions.customFilename}
                onChange={(e) => setExportOptions(prev => ({ ...prev, customFilename: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="bookmarks_export.json"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">选择分类</label>
              <div className="mt-1 max-h-40 overflow-y-auto space-y-2">
                {categories.map(category => (
                  <div key={category.id} className="flex items-center">
                    <Checkbox
                      checked={exportOptions.selectedCategories.includes(category.id)}
                      onChange={(checked) => {
                        setExportOptions(prev => ({
                          ...prev,
                          selectedCategories: checked
                            ? [...prev.selectedCategories, category.id]
                            : prev.selectedCategories.filter(id => id !== category.id)
                        }))
                      }}
                      label={category.name}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">选择标签</label>
              <div className="mt-1 max-h-40 overflow-y-auto space-y-2">
                {allTags.map(tag => (
                  <div key={tag} className="flex items-center">
                    <Checkbox
                      checked={exportOptions.selectedTags.includes(tag)}
                      onChange={(checked) => {
                        setExportOptions(prev => ({
                          ...prev,
                          selectedTags: checked
                            ? [...prev.selectedTags, tag]
                            : prev.selectedTags.filter(t => t !== tag)
                        }))
                      }}
                      label={tag}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {/* 导入预览对话框 */}
      {showImportPreview && importPreview && (
        <Dialog
          title="导入预览"
          onClose={() => setShowImportPreview(false)}
        >
          <div className="space-y-4 p-4">
            <div className="text-sm text-gray-500">
              选择要导入的内容：
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">分类 ({importPreview.categories.length})</h3>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {importPreview.categories.map((category, index) => (
                  <div key={index} className="flex items-center">
                    <Checkbox
                      checked={importPreview.selected.categories.has(index)}
                      onChange={(checked) => {
                        setImportPreview(prev => {
                          if (!prev) return prev
                          const newSelected = new Set(prev.selected.categories)
                          checked ? newSelected.add(index) : newSelected.delete(index)
                          return {
                            ...prev,
                            selected: {
                              ...prev.selected,
                              categories: newSelected
                            }
                          }
                        })
                      }}
                      label={category.name}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">书签 ({importPreview.bookmarks.length})</h3>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {importPreview.bookmarks.map((bookmark, index) => (
                  <div key={index} className="flex items-center">
                    <Checkbox
                      checked={importPreview.selected.bookmarks.has(index)}
                      onChange={(checked) => {
                        setImportPreview(prev => {
                          if (!prev) return prev
                          const newSelected = new Set(prev.selected.bookmarks)
                          checked ? newSelected.add(index) : newSelected.delete(index)
                          return {
                            ...prev,
                            selected: {
                              ...prev.selected,
                              bookmarks: newSelected
                            }
                          }
                        })
                      }}
                      label={bookmark.title}
                    />
                  </div>
                ))}
              </div>
            </div>

            {importPreview.stats.errors.length > 0 && (
              <div className="text-sm text-red-600">
                <h3 className="font-medium mb-1">警告：</h3>
                <ul className="list-disc list-inside">
                  {importPreview.stats.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowImportPreview(false)}
                className="flex items-center"
              >
                <X className="w-4 h-4 mr-1" />
                取消
              </Button>
              <Button
                onClick={handleConfirmImport}
                className="flex items-center"
              >
                <Check className="w-4 h-4 mr-1" />
                确认导入
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {showBrowserImport && (
        <BrowserBookmarkImport onClose={() => setShowBrowserImport(false)} />
      )}
    </>
  )
} 